import { ArrowRightToLine, ExternalLink, Globe, FileText } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { Button } from "../shadcn/button";
import { SourceUrlUIPart, SourceDocumentUIPart } from "ai";
import { WebReferenceMetadata } from "@/generated";
import Link from "next/link";
import ImageWithFallback from "@/components/ImageWithFallback";
import { getWebsiteHostname } from "@/utils/generic";
import { deduplicateSourcesByUrl } from "./utils";
import { useChatDisplayOptionsContext } from "./useChatDisplayOptions";

const SourceCard = ({
  url,
  quote,
  imageUrl,
  index,
}: {
  url: string;
  quote: string;
  imageUrl?: string;
  index: number;
}) => {
  const hostname = getWebsiteHostname(url);
  const iconSrc =
    imageUrl ||
    (hostname
      ? `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`
      : undefined);

  return (
    <Link
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className="group block"
    >
      <div className="border rounded-lg p-3 hover:border-primary/50 transition-all hover:shadow-sm bg-card">
        <div
          className={cn("flex gap-3", quote ? "items-start" : "items-center")}
        >
          <span className="inline-flex items-center justify-center size-5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold shrink-0">
            {index + 1}
          </span>

          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="flex size-6 shrink-0 justify-center items-center border rounded bg-background overflow-hidden">
                    {iconSrc ? (
                      <ImageWithFallback
                        src={iconSrc}
                        fallbackSrc="https://storage.googleapis.com/image-storage-spot-manual/nodes/globe.svg"
                        width={16}
                        height={16}
                        style={{
                          width: "16px",
                          height: "16px",
                          objectFit: "contain",
                        }}
                        alt={hostname}
                      />
                    ) : (
                      <Globe className="size-3 text-muted-foreground" />
                    )}
                  </div>
                  <p
                    className="text-xs text-muted-foreground truncate"
                    title={url}
                  >
                    {url}
                  </p>
                </div>
              </div>
              <ExternalLink className="size-3 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            {quote && (
              <p className="text-sm leading-relaxed line-clamp-3 text-foreground">
                {quote}
              </p>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

const DocumentCard = ({
  title,
  url,
  filename,
  index,
}: {
  title: string;
  url?: string;
  filename?: string | null;
  index: number;
}) => {
  const CardContent = (
    <div className="border rounded-lg p-3 hover:border-primary/50 transition-all hover:shadow-sm bg-card">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center size-5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold shrink-0">
          {index + 1}
        </span>

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="flex size-6 shrink-0 justify-center items-center border rounded bg-background">
                  <FileText className="size-3 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  Document
                </p>
              </div>
            </div>
            {url && (
              <ExternalLink className="size-3 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </div>

          <p className="text-sm leading-relaxed line-clamp-3 text-foreground font-medium">
            {title}
          </p>
          {filename && (
            <p className="text-xs text-muted-foreground truncate">{filename}</p>
          )}
        </div>
      </div>
    </div>
  );

  if (!url) {
    return <div className="group block">{CardContent}</div>;
  }
  return (
    <Link
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className="group block"
    >
      {CardContent}
    </Link>
  );
};

export const SourcesDrawer = ({
  sources,
  onClose,
  floating,
  sourcesClassName,
}: {
  sources: (SourceUrlUIPart | SourceDocumentUIPart)[] | null | undefined;
  onClose: () => void;
  floating: boolean;
  sourcesClassName?: string;
}) => {
  const t = useTranslations("widget.sources");
  const { deduplicateSourcesByUrl: shouldDedup, omitCitationQuotes } =
    useChatDisplayOptionsContext();

  const displaySources = useMemo(
    () => (sources && shouldDedup ? deduplicateSourcesByUrl(sources) : sources),
    [sources, shouldDedup]
  );

  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (sources) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sources, onClose]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  return (
    <div
      ref={drawerRef}
      className={cn(
        "transition-all duration-300 w-0",
        floating && "absolute right-0 top-0 bottom-0 z-50",
        !floating && "relative",
        displaySources && "border-l w-[400px]",
        sourcesClassName
      )}
    >
      {displaySources && (
        <div className="flex flex-col h-full bg-background">
          <div className="flex px-4 py-3 gap-2 justify-start border-b items-center shrink-0">
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("close")}
              onClick={onClose}
              className="size-8"
            >
              <ArrowRightToLine className="size-4" />
            </Button>
            <div className="flex-1">
              <p className="text-sm font-medium">{t("title")}</p>
              <p className="text-xs text-muted-foreground">
                {t("references", { count: displaySources.length })}
              </p>
            </div>
          </div>

          <div className="p-4 overflow-auto space-y-3">
            {displaySources.map((source, index) => {
              if (source.type === "source-url") {
                const meta = source.providerMetadata
                  ?.noxus as WebReferenceMetadata;
                const quote = omitCitationQuotes ? "" : meta?.quote || "";
                const imageUrl = meta?.image_url || undefined;

                return (
                  <SourceCard
                    key={`${source.url}-${index}`}
                    url={source.url}
                    quote={quote}
                    imageUrl={imageUrl}
                    index={index}
                  />
                );
              }

              const docMeta = source.providerMetadata?.noxus as
                | { file_id?: string; quote?: string; url?: string }
                | undefined;
              const documentId = docMeta?.file_id;
              const quote = omitCitationQuotes
                ? ""
                : docMeta?.quote || source.title || "";
              const url =
                docMeta?.url ||
                (documentId
                  ? `/api/backend/file/${documentId}?preview=true`
                  : undefined);

              return (
                <DocumentCard
                  key={`${source.sourceId}-${index}`}
                  title={quote}
                  url={url}
                  filename={source.filename}
                  index={index}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
